import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, ChevronDown, LogOut, Package, UserCircle, X, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

import { useCart } from '@/contexts/CartContext';
import { useAuth } from '../hooks/use-auth';
import { ProductLine, ProductType, SkinType } from '../types/enum';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const { itemCount, loading: cartLoading } = useCart();
  const [showShopMenu, setShowShopMenu] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchValue(params.get('search') ?? '');
  }, [location.pathname, location.search]);

  // Close the mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const params = new URLSearchParams();
    const trimmedSearchValue = searchValue.trim();

    if (trimmedSearchValue) {
      params.set('search', trimmedSearchValue);
    }

    navigate({
      pathname: '/shop',
      search: params.toString() ? `?${params.toString()}` : '',
    });

    setMobileSearchOpen(false);
  };

  if (!user || authLoading) {
    return (
      <div className='bg-white-500 border-b border-border sticky top-0 z-50'>
        <div className='container mx-auto px-4 py-3 md:py-4'>
          <div className='flex items-center justify-between gap-4'>
            <Link to='/' className='flex-shrink-0'>
              <img src='logo2x.png' alt='' className='w-24 md:w-32' />
            </Link>
            <MobileMenuButton open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} />
          </div>
        </div>
        <div className='flex-1 w-full bg-primary h-8 hidden md:block'></div>
      </div>
    );
  }

  return (
    <header className='bg-white border-b border-border sticky top-0 z-50'>
      {/* Top bar with logo and search */}
      <div className='container mx-auto px-4 py-3 md:py-4'>
        <div className='flex items-center justify-between gap-4'>
          <Link to='/' className='flex-shrink-0'>
            <img src='logo2x.png' alt='' className='w-24 md:w-32' />
          </Link>

          {/* Search - desktop */}
          <div className='flex-1 max-w-xl hidden md:block'>
            <form className='relative' onSubmit={handleSearchSubmit}>
              <Input
                type='search'
                placeholder='Хайлт'
                className='w-full pr-10'
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
              />
              <button
                type='submit'
                aria-label='Search products'
                className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground'
              >
                <Search className='h-4 w-4' />
              </button>
            </form>
          </div>

          <div className='flex items-center gap-2 md:gap-4'>
            {/* Search icon for mobile */}
            <Button
              variant='ghost'
              size='icon'
              className='md:hidden'
              onClick={() => setMobileSearchOpen((prev) => !prev)}
            >
              {mobileSearchOpen ? <X className='h-5 w-5' /> : <Search className='h-5 w-5' />}
            </Button>

            {/* Hamburger menu for mobile */}
            <div className='md:hidden'>
              <MobileMenuButton open={mobileMenuOpen} onOpenChange={setMobileMenuOpen} />
            </div>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='ghost' size='icon' className='hidden md:flex'>
                    <User className='h-5 w-5' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-48 bg-background'>
                  <DropdownMenuItem asChild>
                    <Link to='/profile' className='flex items-center gap-2 cursor-pointer'>
                      <UserCircle className='h-4 w-4' />
                      Профайл
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to='/orders' className='flex items-center gap-2 cursor-pointer'>
                      <Package className='h-4 w-4' />
                      Захиалгууд
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className='flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive'
                  >
                    <LogOut className='h-4 w-4' />
                    Гарах
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to='/auth' className='hidden md:block'>
                <Button variant='ghost' size='icon'>
                  <User className='h-5 w-5' />
                </Button>
              </Link>
            )}
            {/* Cart - hidden on mobile (in tab bar instead) */}
            <Link to='/cart' className='relative hidden md:block'>
              <Button variant='ghost' size='icon'>
                <ShoppingCart className='h-5 w-5' />
                {itemCount > 0 && (
                  <span className='absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-medium rounded-full min-w-[20px] h-5 flex items-center justify-center animate-in fade-in zoom-in duration-200'>
                    {itemCount}
                  </span>
                )}
                {cartLoading && itemCount === 0 && (
                  <span className='absolute -top-1 -right-1 bg-muted text-muted-foreground text-xs rounded-full w-5 h-5 animate-pulse' />
                )}
              </Button>
            </Link>
          </div>
        </div>

        {mobileSearchOpen && (
          <form className='relative mt-3 md:hidden' onSubmit={handleSearchSubmit}>
            <Input
              type='search'
              placeholder='Хайлт'
              className='w-full pr-10'
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value.toLowerCase())}
              autoFocus
            />
            <button
              type='submit'
              aria-label='Search products'
              className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground'
            >
              <Search className='h-4 w-4' />
            </button>
          </form>
        )}
      </div>
      {/* Navigation - hidden on mobile */}
      <nav className='bg-primary text-primary-foreground hidden md:block'>
        <div className='container mx-auto px-4'>
          <ul className='flex items-center gap-6 text-sm'>
            <li>
              <Link to='/' className='block py-3 hover:opacity-80 transition-opacity'>
                Нүүр
              </Link>
            </li>
            <li
              className='relative'
              onMouseEnter={() => setShowShopMenu(true)}
              onMouseLeave={() => setShowShopMenu(false)}
            >
              <button className='flex items-center gap-1 py-3 hover:opacity-80 transition-opacity'>
                Бүтээгдэхүүн
                <ChevronDown className='h-4 w-4' />
              </button>

              {showShopMenu && (
                <div className='absolute left-0 top-full bg-white text-foreground shadow-lg border border-border w-[800px] p-8'>
                  <div className='grid grid-cols-3 gap-8'>
                    <div>
                      <h3 className='font-semibold mb-4 text-sm uppercase tracking-wide'>Skin type</h3>
                      <ul className='space-y-2'>
                        {Object.values(SkinType).map((type) => (
                          <li key={type}>
                            <Link
                              to={`/shop?skinType=${type}`}
                              className='text-sm text-muted-foreground hover:text-primary transition-colors'
                            >
                              {type
                                .toLowerCase()
                                .replace('_', ' ')
                                .split(' ')
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className='font-semibold mb-4 text-sm uppercase tracking-wide'>Product line</h3>
                      <ul className='space-y-2'>
                        {Object.values(ProductLine).map((line) => (
                          <li key={line}>
                            <Link
                              to={`/shop?productLine=${line.toLowerCase().replace(' ', '-')}`}
                              className='text-sm text-muted-foreground hover:text-primary transition-colors'
                            >
                              {line
                                .toLowerCase()
                                .replace('_', ' ')
                                .split(' ')
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className='font-semibold mb-4 text-sm uppercase tracking-wide'>Product type</h3>
                      <ul className='space-y-2'>
                        {Object.values(ProductType).map((type) => (
                          <li key={type}>
                            <Link
                              to={`/shop?productType=${type.toLowerCase().replace(' ', '-')}`}
                              className='text-sm text-muted-foreground hover:text-primary transition-colors'
                            >
                              {type
                                .toLowerCase()
                                .replace('_', ' ')
                                .split(' ')
                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ')}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </li>
            {/* <li>
              <Link
                to='/knowledge'
                className='block py-3 hover:opacity-80 transition-opacity'
              >
                Гарын авлага
              </Link>
            </li> */}
            <li>
              <Link to='/academy' className='block py-3 hover:opacity-80 transition-opacity'>
                Сургалт
              </Link>
            </li>
            <li>
              <Link to='/media' className='block py-3 hover:opacity-80 transition-opacity'>
                Хэрэгцээт файлууд
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

const mobileNavItems = [
  { path: '/', label: 'Нүүр' },
  { path: '/shop', label: 'Дэлгүүр' },
  { path: '/academy', label: 'Сургалт' },
  { path: '/media', label: 'Хэрэгцээт файлууд' },
];

/** Hamburger + sheet menu shown on mobile only (links mirror the desktop nav). */
const MobileMenuButton = ({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetTrigger asChild>
      <Button variant='ghost' size='icon' aria-label='Цэс'>
        <Menu className='h-5 w-5' />
      </Button>
    </SheetTrigger>
    <SheetContent side='right' className='w-72'>
      <SheetHeader>
        <SheetTitle>Цэс</SheetTitle>
      </SheetHeader>
      <nav className='flex flex-col gap-1 px-4'>
        {mobileNavItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className='rounded-md px-3 py-2.5 text-sm font-medium text-foreground hover:bg-muted hover:text-primary transition-colors'
            onClick={() => onOpenChange(false)}
          >
            {item.label}
          </Link>
        ))}
      </nav>
    </SheetContent>
  </Sheet>
);

export default Header;
